import React, { memo, useState, useCallback, useEffect, useRef } from 'react';
import { Tooltip } from '@mui/material';
import './ThinkingBlock.css';

/**
 * ThinkingBlock component for displaying AI reasoning/thinking process
 * Similar to ChatGPT and Claude's thinking display
 */
const ThinkingBlock = memo(({
    thinkingId,
    content,
    isThinking,
    durationMs,
    summary
}) => {
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [displayDuration, setDisplayDuration] = useState(0);
    const startTimeRef = useRef(Date.now());

    // Update display duration while thinking
    useEffect(() => {
        if (isThinking) {
            startTimeRef.current = Date.now();
            const interval = setInterval(() => {
                setDisplayDuration(Math.floor((Date.now() - startTimeRef.current) / 1000));
            }, 1000);
            return () => clearInterval(interval);
        } else if (durationMs) {
            setDisplayDuration(Math.floor(durationMs / 1000));
        }
    }, [isThinking, durationMs]);

    // Auto-collapse when thinking completes
    useEffect(() => {
        if (!isThinking && content) {
            // Collapse after a short delay when thinking completes
            const timeout = setTimeout(() => {
                setIsCollapsed(true);
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [isThinking, content]);

    const toggleCollapse = useCallback(() => {
        setIsCollapsed(prev => !prev);
    }, []);

    const formatDuration = (seconds) => {
        if (seconds < 60) {
            return `${seconds}s`;
        }
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}m ${remainingSeconds}s`;
    };

    const headerText = isThinking
        ? `Thinking...`
        : `Thought for ${formatDuration(displayDuration)}`;

    return (
        <div className={`ntb-thinking-block ${isThinking ? 'thinking' : 'completed'} ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="ntb-thinking-header" onClick={toggleCollapse}>
                <div className="ntb-thinking-icon">
                    {isThinking ? (
                        <svg className="ntb-thinking-spinner" viewBox="0 0 24 24" width="16" height="16">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.4" strokeDashoffset="10" />
                        </svg>
                    ) : (
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                        </svg>
                    )}
                </div>
                <span className="ntb-thinking-title">{headerText}</span>
                <Tooltip title={isCollapsed ? "Expand" : "Collapse"} arrow placement="top">
                    <button className="ntb-thinking-toggle" onClick={toggleCollapse} aria-label={isCollapsed ? "Expand thinking" : "Collapse thinking"}>
                        <svg
                            viewBox="0 0 24 24"
                            width="16"
                            height="16"
                            fill="currentColor"
                            style={{ transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s' }}
                        >
                            <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                        </svg>
                    </button>
                </Tooltip>
            </div>
            {!isCollapsed && (
                <div className="ntb-thinking-content">
                    {content || (isThinking ? "Processing your request..." : "")}
                    {isThinking && <span className="ntb-thinking-cursor">|</span>}
                </div>
            )}
        </div>
    );
});

ThinkingBlock.displayName = 'ThinkingBlock';

export default ThinkingBlock;
